/**
 * Items Store
 *
 * Manages all items in the prioritization system. All work objects are items from creation.
 * Priority is contextual (membership + rank in a list), not a lifecycle state.
 *
 * DB-first persistence:
 * - Primary and durable source of truth: Supabase
 * - In-memory state is a working cache and must not create an alternate durable reality
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing `any` usage throughout this file, unrelated to this change; out of scope to retype here */

import { create } from 'zustand';
// Removed persist - Supabase is now the primary data store
import type { Item, ShapedItem, Priority, AttributeValue, LifecycleState, ItemFrame, ItemLanguageType, Candidate } from '../../types/prioritization';
import { migrateItem } from '../../lib/stateMigration';
import { isUuid } from '../../lib/isUuid';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import {
  fetchAll,
  fetchById,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  fromDbFormat,
  type SyncState,
} from '../../lib/supabaseSync';
import {
  mustHave,
} from '../../lib/permissionEnforcement';
import {
  checkPolicyForItem,
  askAgainAfterPermissionsLoad,
} from '../../lib/authorization/storeEnforcement';
import { getReasonMessage } from '../../lib/authorization/reasonMessages';
import { retireNewItem, type ReversalResult } from '../../lib/reversals/reversalsService';
import { getRootQueueIdForWorkspace } from '../../lib/queueRouting';
import { computeLegacyReadinessMirror, isItemReadyForPrioritization } from '../../lib/itemReadiness';
import { useUserStore } from './userStore';
import { useAuditStore } from './auditStore';
import { itemFieldEventType } from '../../lib/itemFieldEventType';
import { promoteSessionItems as promoteSessionItemsLib } from '../../lib/promotion';
// Lazy references to avoid circular deps at parse time — all stores are
// module-level singletons safe to access after initialisation.
import { useSpaceStore } from './spaceStore';
import { resolveOwnerOnQueueMove } from '../../lib/queueRouting';

const TABLE_NAME = 'items';
const ITEM_FRAMES_TABLE = 'item_frames';

const DEMO_USER_UUID = '77777777-7777-7777-7777-777777777777';

function isRealUserUUID(value: string | null | undefined): value is string {
  return !!value && /^[0-9a-f-]{36}$/i.test(value) && value !== DEMO_USER_UUID;
}

/**
 * True when a write was rejected because items.space_id points at a space row
 * that no longer exists — the fingerprint of a deleted space (or a workspace
 * reseed, which drops and recreates spaces with fresh UUIDs) while a client
 * still holds the old id in its persisted selection.
 */
function isDeadSpaceReference(error: unknown): boolean {
  const text = String(
    (error as { message?: string } | null)?.message ?? error ?? ''
  );
  return text.includes('items_space_id_fkey');
}

// Stable empty array to avoid creating new references in getters
const EMPTY_FRAMES: ItemFrame[] = [];

interface ItemFrameRow {
  id: string;
  item_id: string;
  frame_type: ItemLanguageType;
  title: string;
  description: string;
  is_primary: boolean;
  is_original?: boolean | null;
  created_at: string;
  created_by?: string | null;
  altitude_scoping_inputs?: Record<string, string> | null;
  altitude_level?: string | null;
}

interface ItemFrameDbRecord {
  id: string;
  itemId: string;
  frameType: ItemLanguageType;
  title: string;
  description: string;
  isPrimary: boolean;
  isOriginal?: boolean | null;
  createdAt: Date;
  createdBy?: string | null;
  altitudeScopingInputs?: Record<string, string> | null;
  altitudeLevel?: string | null;
}

function frameDbRecordToDomain(record: ItemFrameDbRecord): ItemFrame {
  return {
    id: record.id,
    sourceItemId: record.itemId,
    frameType: record.frameType,
    title: record.title,
    description: record.description,
    createdBy: record.createdBy ?? 'unknown',
    createdAt: record.createdAt,
    isCanonical: !!record.isPrimary,
    isOriginal: record.isOriginal ?? undefined,
    altitudeScopingInputs: record.altitudeScopingInputs ?? undefined,
    altitudeLevel: record.altitudeLevel ?? undefined,
  };
}

function attachFramesToItems(items: Item[], frameRows: ItemFrameRow[]): Item[] {
  const framesByItemId = new Map<string, ItemFrame[]>();
  for (const row of frameRows) {
    const frame = frameDbRecordToDomain(fromDbFormat<ItemFrameDbRecord>(row as unknown as Record<string, unknown>));
    const existing = framesByItemId.get(frame.sourceItemId) ?? [];
    existing.push(frame);
    framesByItemId.set(frame.sourceItemId, existing);
  }

  return items.map((item) => {
    const frames = framesByItemId.get(item.id) ?? EMPTY_FRAMES;
    const canonicalFrame = frames.find((frame) => frame.isCanonical);
    return {
      ...item,
      frames: frames.length ? frames : undefined,
      canonicalFrameId: canonicalFrame?.id,
    };
  });
}

function syncSubmissionReadyStatus(itemId: string): void {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  // Supabase's query builder is PromiseLike (has .then) but not a full
  // Promise, so .catch() isn't available directly — wrap with Promise.resolve.
  void Promise.resolve(
    supabase
      .from('idea_submissions')
      .update({ status: 'READY' })
      .eq('promoted_to_item_id', itemId)
  )
    .then(({ error }) => {
      if (error) {
        console.debug('[ItemsStore] Could not update submission status:', error.message);
      }
    })
    .catch((error: unknown) => {
      console.debug('[ItemsStore] Submission status update skipped:', error);
    });
}

/** Normalize attributes array to a map key -> value. */
function attrsToMap(attrs: unknown): Record<string, unknown> {
  if (!Array.isArray(attrs)) return {};
  const map: Record<string, unknown> = {};
  for (const entry of attrs) {
    if (entry && typeof entry === 'object' && 'attributeKey' in entry && typeof (entry as { attributeKey: string }).attributeKey === 'string') {
      const key = (entry as { attributeKey: string; value?: unknown }).attributeKey;
      map[key] = (entry as { value?: unknown }).value;
    }
  }
  return map;
}

/**
 * If the attributes change represents exactly one attribute value change (or one add/remove), return { attributeKey, from, to }.
 * Otherwise return null so we log the full attributes diff.
 */
function getSingleAttributeChange(from: unknown, to: unknown): { attributeKey: string; from: unknown; to: unknown } | null {
  const oldMap = attrsToMap(from);
  const newMap = attrsToMap(to);
  const allKeys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);
  const changed: { key: string; from: unknown; to: unknown }[] = [];
  for (const key of allKeys) {
    const ov = oldMap[key];
    const nv = newMap[key];
    if (ov !== nv) changed.push({ key, from: ov, to: nv });
  }
  if (changed.length !== 1) return null;
  return { attributeKey: changed[0].key, from: changed[0].from, to: changed[0].to };
}

/**
 * If a metadata (custom-field) change represents exactly one key change, return { attributeKey, from, to }.
 * metadata is already a flat Record<string, unknown>, unlike attributes which is an array.
 */
function getSingleMetadataChange(from: unknown, to: unknown): { attributeKey: string; from: unknown; to: unknown } | null {
  const oldMap = (from && typeof from === 'object' && !Array.isArray(from)) ? (from as Record<string, unknown>) : {};
  const newMap = (to && typeof to === 'object' && !Array.isArray(to)) ? (to as Record<string, unknown>) : {};
  const allKeys = new Set([...Object.keys(oldMap), ...Object.keys(newMap)]);
  const changed: { key: string; from: unknown; to: unknown }[] = [];
  for (const key of allKeys) {
    const ov = oldMap[key];
    const nv = newMap[key];
    if (JSON.stringify(ov) !== JSON.stringify(nv)) changed.push({ key, from: ov, to: nv });
  }
  if (changed.length !== 1) return null;
  return { attributeKey: changed[0].key, from: changed[0].from, to: changed[0].to };
}

/**
 * Extract only fields that exist in the Supabase items table schema.
 * 
 * HOW TO UPDATE:
 * 1. Go to Settings > Data Diagnostics
 * 2. Copy the SQL shown for missing columns
 * 3. Run in Supabase SQL Editor
 * 4. Uncomment the corresponding lines below
 * 5. Refresh Data Diagnostics to confirm
 * 
 * This prevents "column not found" errors when syncing.
 */
/**
 * Build Supabase record with ALL columns that exist in the database.
 * items table columns: id, title, description, lifecycle_state, exit_status,
 * altitude, created_by, created_at, updated_at, source, tags, workspace_id, org_unit_id, queue_id,
 * item_category, parent_item_id, dedupe_status, duplicate_of_id, etc.
 *
 * exit_status (active/deferred/archived) is the canonical state field.
 * detailed_state and lifecycle_stage are kept for backward compat but are no longer primary.
 */
/**
 * Turn a raw Supabase insert error into a message a person can act on.
 * A row-level-security rejection almost always means the session isn't
 * authenticated (no auth.uid()) or the user can't write to this workspace.
 * The item is NOT kept: a failed create is rolled back, so the message must
 * never claim it was saved anywhere.
 */
function humanizeSyncError(error: string): string {
  if (/row-level security|violates row-level/i.test(error)) {
    return 'The item was not saved — you are signed out or do not have permission to add items to this workspace.';
  }
  return `The item was not saved: ${error}`;
}

/**
 * Record one change to an item in its history (audit_events), the same way updateItem
 * records its tracked fields. For writes that do not go through updateItem (owner,
 * delegation, additional categories…). Call it only after the write succeeded.
 */
export function recordItemFieldChange(itemId: string, field: string, from: unknown, to: unknown): void {
  try {
    useAuditStore.getState().logShapingEvent({
      type: itemFieldEventType(field) as never,
      itemId,
      actorId: useUserStore.getState().userId || 'unknown',
      actorRole: 'editor',
      details: { field, from, to },
    });
  } catch (e) {
    console.warn('[ItemsStore] Failed to record history:', e);
  }
}

/** A field's value by its snake_case key when that key is present (null included), else its camelCase copy. */
function snakeOrCamel(item: object, snake: string, camel: string): string | null {
  const rec = item as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(rec, snake)) return (rec[snake] as string | null | undefined) ?? null;
  return (rec[camel] as string | null | undefined) ?? null;
}

function getSupabaseCompatibleFields(item: Partial<Item>): Record<string, unknown> {
  // Convert dates to ISO strings
  const toISOOrNull = (d: Date | undefined | null): string | null => {
    if (!d) return null;
    if (d instanceof Date) return d.toISOString();
    return d;
  };
  
  // Canonical creator: real user UUID only (never the demo placeholder)
  const createdByUserId =
    item.created_by_user_id ??
    (isRealUserUUID(item.createdBy) ? item.createdBy : null);
  // Legacy raw DB field (not part of the canonical Item shape) that can leak
  // through on rows fetched straight from Supabase before normalization.
  const legacyCreatedBy = (item as { created_by?: string }).created_by;
  const createdByText =
    createdByUserId ??
    (legacyCreatedBy && legacyCreatedBy !== DEMO_USER_UUID ? legacyCreatedBy : '');

  const fields: Record<string, unknown> = {
    // Core required fields
    id: item.id,
    title: item.title || '',
    description: item.description || '',
    lifecycle_state: item.lifecycle_state ?? 'item',
    exit_status: (item as any).exit_status ?? 'active',
    // Legacy columns kept for backward compat — not primary state
    lifecycle_stage: item.lifecycle_stage || 'intake',
    detailed_state: (item as any).detailedState || null,
    created_by: createdByText,
    created_by_user_id: createdByUserId ?? null,
    created_at: toISOOrNull(item.createdAt) || new Date().toISOString(),
    updated_at: toISOOrNull(item.updatedAt) || new Date().toISOString(),
    
    // Classification
    altitude: item.altitude || null, // @deprecated - use altitude_level
    item_category: item.itemCategory || null,
    classification_status: (item as any).classificationStatus || 'empty',
    creation_source:
      (item as any).creationSource ||
      ((item as any).creationProvenance === 'ai_draft' ||
      (item as any).creationProvenance === 'ai_approved'
        ? 'ai'
        : 'human'),
    creation_review_status:
      (item as any).creationReviewStatus ||
      ((item as any).creationProvenance === 'ai_approved' ? 'human_reviewed' : 'none'),
    // Legacy compressed column — also synced by DB trigger from source×review.
    creation_provenance: (item as any).creationProvenance || 'human',
    dedupe_status: item.dedupeStatus || 'pending',
    tags: item.tags || [],
    source: item.source || null,
    
    // Altitude governance fields.
    altitude_level: item.altitudeLevel || null, // 1-5 numeric level
    declared_altitude: item.declaredAltitude || null, // @deprecated
    canonical_altitude: item.canonicalAltitude || null, // @deprecated
    altitude_lifecycle_state: item.altitudeLifecycleState || 'proposed',
    altitude_scoping_inputs: item.altitudeScopingInputs || null,
    altitude_frame: item.altitudeFrame || null,
    altitude_completion_requirements: item.altitudeCompletionRequirements || null,
    effort_capacity_units:
      item.effortCapacityUnits !== undefined && item.effortCapacityUnits !== null
        ? item.effortCapacityUnits
        : null,
    effort_source: item.effortSource ?? null,
    
    // Leadership approval
    leadership_approved: item.leadershipApproved || false,
    leadership_approved_at: item.leadershipApprovedAt || null,
    leadership_approved_by: item.leadershipApprovedBy || null,
    
    // Organization & routing (canonical in-memory field is workspaceId; map to workspace_id for DB)
    workspace_id: item.workspaceId ?? item.workspace_id ?? null,
    org_unit_id: item.orgUnitId || null,
    queue_id: item.queueId || null,
    product_node_id: item.productNodeId || null,
    
    // Hierarchy
    parent_item_id: item.parentItemId || null,
    duplicate_of_id: item.duplicateOfId || null,
    split_from_id: item.splitFromId || null,
    
    // Item-work ownership and progress fields.
    shaping_owner_id: item.shapingOwnerId || null,
    shaping_started_at: toISOOrNull(item.shapingStartedAt),
    shaped_at: toISOOrNull(item.shapedAt),
    
    // Readiness
    ready_for_ranking: item.readyForRanking || false,
    shaping_completeness: item.shapingCompleteness || 0,
    
    // OST (Opportunity Solution Tree) integration
    ost_node_id: item.ostNodeId || null,
    ost_opportunity_id: item.ostOpportunityId || null,

    // Promotion provenance (Stage 3)
    promotion_cohort_id: item.promotionCohortId ?? null,
    source_session_id: item.sourceSessionId ?? null,
    promoted_by: item.promotedBy ?? null,
    promoted_at: toISOOrNull(item.promotedAt),

    // Ownership
    owner_id: item.ownerId ?? null,
    delegate_id: item.delegateId ?? null,
    ownership_mode: item.ownershipMode ?? 'direct',

    // Deferral metadata
    deferred_at: toISOOrNull(item.deferredAt ?? null),
    defer_review_date: toISOOrNull(item.deferReviewDate ?? null),
    // Only sent when it was written: a whole-row update must not clear a reason the client never loaded.
    ...(item.deferReason !== undefined ? { defer_reason: item.deferReason } : {}),

    // Measure and reason (the boxes on the item page). Only sent
    // when present on the item, so a whole-row update from a row that never loaded
    // them cannot clear them.
    ...(item.evidenceOfSuccess !== undefined ? { evidence_of_success: item.evidenceOfSuccess } : {}),
    ...(item.whyNow !== undefined ? { why_now: item.whyNow } : {}),

    // Work type — registry-controlled execution classification (Canon Doc 21, migration 289).
    // Nullable; null = recipe_pending once the item enters Planning. Hydrated on load via
    // select('*'), so passing it through here preserves (never clobbers) imported values.
    // The snake_case key wins whenever it is present — even as null — so clearing a type
    // clears it. Rows loaded through fetchAll also carry a camelCase copy (workType…);
    // reading it as a `??` fallback put the old value back on every clear (2026-10-15).
    work_type: snakeOrCamel(item, 'work_type', 'workType'),
    launch_type: snakeOrCamel(item, 'launch_type', 'launchType'),
    investment_type: snakeOrCamel(item, 'investment_type', 'investmentType'),
    external_id: snakeOrCamel(item, 'external_id', 'externalId'),

    // Planning maturity — Directional vs Detailed Planning signal
    planning_maturity:
      item.planning_maturity ??
      (item as { planningMaturity?: string }).planningMaturity ??
      'directional',

    // Uncertainty encoding — epistemic quality of Directional Planning inputs (migration 413)
    knowledge_status:
      item.knowledge_status ??
      (item as { knowledgeStatus?: string }).knowledgeStatus ??
      'unknown',
    confidence_level:
      item.confidence_level ??
      (item as { confidenceLevel?: string }).confidenceLevel ??
      'low',

    // Collaboration space (migration 471). NULL = org-level.
    space_id: item.spaceId ?? null,

    // Product module origin (migration 490)
    origin_module: (item as Item & { originModule?: string }).originModule ?? 'prioritization',
  };

  // Custom field values live in items.metadata (JSONB). Only include when
  // the caller has set metadata, so we don't overwrite existing values with
  // undefined on partial updates.
  if (item.metadata !== undefined) {
    fields.metadata = item.metadata ?? null;
  }

  // Migration 751 markers — set only at creation, so only sent when present
  // (a partial update must never clear them).
  if (item.submittedVia) {
    fields.submitted_via = item.submittedVia;
  }
  const importBatchId =
    item.importBatchId ?? (item as { import_batch_id?: string | null }).import_batch_id ?? null;
  if (importBatchId) {
    fields.import_batch_id = importBatchId;
  }

  return fields;
}

/** Result of retiring one item through `retire_new_item`. */
export interface RetireItemOutcome {
  ok: boolean;
  /** Plain refusal text, absent on success. */
  reason?: string;
  /** Set when the item is already in use (pool, session, plan, decision, relationships). */
  blockedBy?: string;
  /** What the server suggests instead, when it offered one. */
  remedy?: string;
}

/**
 * What one item write did, once the server answered. `updateItem` and
 * `assignShapingOwner` apply the change locally first (optimistic) and then
 * report whether the row was saved, so a bulk caller can say "11 of 14 saved"
 * instead of announcing success it never checked. With no database configured
 * the local change is the whole truth and the write reports ok.
 */
export type ItemWriteOutcome = { ok: true } | { ok: false; reason: string };

export interface RetireItemFailure {
  id: string;
  title?: string;
  reason: string;
  blockedBy?: string;
  remedy?: string;
}

interface ItemsState {
  items: Item[];
  /** Workspace id for the last successful fetchFromSupabase; null until then. Drives home/dashboard gating when workspace context catches up after items sync. */
  itemsFetchWorkspaceId: string | null;

  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  syncToSupabase: (item: Item) => Promise<{ success: boolean; error?: string }>;
  
  // Actions
  /**
   * Create an item (optimistic local + Supabase insert).
   * Resolves true when the insert succeeds (or Supabase is unavailable);
   * false when policy blocks or the insert fails. Callers that create
   * relationships/memberships immediately after should await this.
   */
  // NOTE: 'id' must be in the Omit key list, not just re-added via the `&`
  // intersection — Omit<Item, ...> alone still carries Item's required
  // `id: string`, and intersecting with `{ id?: string }` does not widen an
  // already-required property to optional. Without 'id' in the Omit, every
  // caller that (correctly) omits id from its create payload fails to
  // typecheck even though createItem's own implementation already defaults
  // it via generateUUID() when absent.
  createItem: (data: Omit<Item, 'id' | 'createdAt' | 'updatedAt' | 'lifecycle_state' | 'lifecycle_stage'> & { id?: string }) => Promise<boolean>;
  /**
   * Same create as `createItem` (policy check, space/queue stamping, save,
   * auto-classification), but returns the reason when it fails and the new id
   * when it succeeds, so callers can tell the user what happened.
   */
  createItemWithReason: (
    data: Omit<Item, 'id' | 'createdAt' | 'updatedAt' | 'lifecycle_state' | 'lifecycle_stage' | 'queueId'> & {
      id?: string;
      /**
       * Omit to route to the workspace's root queue (the default). Pass `null`
       * to save the item with no queue at all — Submit Idea does this when the
       * submitter leaves the queue blank.
       */
      queueId?: string | null;
    },
  ) => Promise<{ ok: boolean; id?: string; reason?: string }>;
  /** Create an item from an OST "Import to Cycle" payload and return the created item for pool use. */
  addCandidate: (data: {
    title: string;
    description?: string;
    candidateType?: string;
    source?: string;
    ostNodeId?: string;
    ostOpportunityId?: string;
    createdBy?: string;
    submittedBy?: string;
    metadata?: Record<string, unknown>;
  }) => Item | undefined;
  updateItem: (id: string, updates: Partial<Item | Priority>, options?: { phase?: string }) => Promise<ItemWriteOutcome>;
  /**
   * Move an item to a different workspace.
   * Enforces Canon §F02: blocked if the item is in any active (non-PUBLISHED) session pool.
   * This is the ONLY sanctioned path for workspace reassignment.
   */
  moveItemToWorkspace: (itemId: string, targetWorkspaceId: string, movedBy: string) => Promise<{ success: boolean; error?: string }>;
  /**
   * Retire one item (owner ruling 2026-09-22: users never hard-delete items).
   * Goes through the governed `retire_new_item` RPC (mig 746): the item leaves
   * every list (exit_status → archived) but its record, closure event and
   * lineage stay. A written reason is required. When something already uses
   * the item the server refuses and names it in `blockedBy` — the caller then
   * offers the normal archive path instead. Never throws.
   */
  retireItemWithReason: (id: string, reason: string) => Promise<RetireItemOutcome>;
  /**
   * Retire several items with one reason and report what actually happened,
   * so bulk callers never announce "N retired" when some were refused.
   */
  retireItems: (ids: string[], reason: string) => Promise<{
    retired: string[];
    failed: RetireItemFailure[];
  }>;
  
  splitItem: (sourceId: string, childTitles: string[], rationale?: string) => Promise<{ success: boolean; childIds: string[]; error?: string }>;

  /** Create a lineage event (retire, supersede, split, merge, fork). */
  createLineageEvent: (params: {
    eventType: 'retire' | 'supersede' | 'split' | 'merge' | 'fork';
    sourceItemIds: string[];
    targetItemIds: string[];
    rationale?: string;
  }) => Promise<{ success: boolean; lineageEventId?: string; error?: string }>;
  /** Retire an item (no successor). */
  retireItem: (itemId: string, rationale?: string) => Promise<{ success: boolean; error?: string }>;
  /** Supersede an item by another (replacement). */
  supersedeItem: (sourceItemId: string, targetItemId: string, rationale?: string) => Promise<{ success: boolean; error?: string }>;
  /** Fork an item — source stays active, new item is a clone with new title. */
  forkItem: (sourceId: string, forkTitle: string, rationale?: string) => Promise<{ success: boolean; forkId?: string; error?: string }>;
  /** Merge multiple items — all sources archived, one new item created. */
  mergeItems: (sourceIds: string[], mergedTitle: string, rationale?: string) => Promise<{ success: boolean; mergedId?: string; error?: string }>;

  // Lifecycle promotions
  promoteToItem: (id: string, attributes: AttributeValue[]) => void;
  promoteToPriority: (id: string, itemPoolId: string, rank: number) => void;
  /** Stage 3: Promote session items (write event + set provenance on items). Refreshes from Supabase on success. */
  promoteSessionItems: (sessionId: string, itemIds: string[], promotedBy: string) => Promise<{ success: boolean; error?: string; promotionCohortId?: string; itemCount?: number; succeededIds?: string[]; skippedIds?: string[] }>;
  
  // Leadership approval
  leadershipApprovePriority: (id: string, approvedBy: string) => void;
  revokeLeadershipApproval: (id: string) => void;
  
  // Attribute management
  updateAttributes: (id: string, attributes: AttributeValue[]) => void;
  /** Legacy compatibility field for older completeness-driven flows. */
  updateShapingCompleteness: (id: string, completeness: number) => void;
  
  // Assignee management
  assignShapingOwner: (id: string, userId: string) => Promise<ItemWriteOutcome>;
  /** @deprecated Use assignShapingOwner */
  assignSteward: (id: string, userId: string) => Promise<ItemWriteOutcome>;

  // Ownership management
  /** Transfer full ownership to a new user. Clears any existing delegation. Reports whether the row saved. */
  transferOwnership: (id: string, newOwnerId: string) => Promise<ItemWriteOutcome>;
  /** Delegate the item to another user while the original owner retains ownership. */
  delegateOwnership: (id: string, delegateId: string) => Promise<void>;
  /** Reclaim ownership — remove the delegate and return to direct mode. */
  reclaimOwnership: (id: string) => Promise<void>;

  // Frame management
  addFrame: (itemId: string, frame: Omit<ItemFrame, 'id' | 'sourceItemId' | 'createdAt'>) => string;
  updateFrame: (itemId: string, frameId: string, updates: Partial<ItemFrame>) => void;
  removeFrame: (itemId: string, frameId: string) => void;
  setCanonicalFrame: (itemId: string, frameId: string) => void;
  /** Use the item's own title/description/type as the active framing (clears canonicalFrameId) */
  setUseItemAsCanonical: (itemId: string) => void;
  getFramesForItem: (itemId: string) => ItemFrame[];
  getCanonicalFrame: (itemId: string) => ItemFrame | undefined;
  /** Find an existing frame of the given type, or create one and make it canonical */
  findOrCreateFrameForType: (itemId: string, frameType: ItemLanguageType) => string;
  /** Persist altitude scoping inputs and computed level to a specific frame */
  saveFrameAltitude: (itemId: string, frameId: string, scopingInputs: Record<string, string>, altitudeLevel?: string) => void;
  
  // Clarification helpers
  updateHasPendingClarifications: (id: string, hasPending: boolean) => void;
  
  // Getters
  getItemById: (id: string) => Item | undefined;
  loadItemById: (id: string) => Promise<void>;
  getUnreadyItems: () => Item[];
  getItems: () => ShapedItem[];
  getPriorities: () => Priority[];
  getItemsByState: (state: LifecycleState) => Item[];
}

// Track if initial fetch has been triggered (module-level to persist across renders)
export const useItemsStore = create<ItemsState>()(
  (set, get) => ({
  items: [],
  itemsFetchWorkspaceId: null,
  syncState: createInitialSyncState(),
  
  // ========================================================================
  // Supabase Sync Actions
  // ========================================================================
  
  fetchFromSupabase: async () => {
    if (!isSupabaseAvailable()) {
      console.log('[ItemsStore] Supabase not available');
      return;
    }

    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
    
    console.log('[ItemsStore] Fetching from Supabase with strict workspace filter...');
    const result = await fetchAll<Item>(TABLE_NAME, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true });
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    let frameRows: ItemFrameRow[] = [];
    if (supabase && workspaceId) {
      const { data: frameData, error: frameError } = await supabase
        .from(ITEM_FRAMES_TABLE)
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: true });
      if (frameError) {
        console.error('[ItemsStore] Failed to fetch item frames from Supabase:', frameError.message);
      } else {
        frameRows = (frameData as ItemFrameRow[] | null) ?? [];
      }
    }
    
    if (result.error) {
      set({ 
        syncState: { 
          ...get().syncState, 
          isLoading: false, 
          syncError: result.error 
        } 
      });
      console.error('[ItemsStore] Failed to fetch from Supabase:', result.error);
      return;
    }
    
    if (result.data) {
      // Run migration on fetched data
      const migrated = attachFramesToItems(result.data.map(c => migrateItem(c)), frameRows);
      
      // Compare fetched rows with the current in-memory cache for the same DB-backed items.
      const existingItems = get().items;
      const existingById = new Map(existingItems.map(c => [c.id, c]));
      
      // Prefer the newer representation when the same DB-backed item exists in memory.
      const mergedFromSupabase = migrated.map(supabaseItem => {
        const localItem = existingById.get(supabaseItem.id);
        if (!localItem) return supabaseItem;
        
        // Compare timestamps for the same persisted item.
        const localUpdatedAt = localItem.updatedAt instanceof Date 
          ? localItem.updatedAt.getTime() 
          : new Date(localItem.updatedAt).getTime();
        const supabaseUpdatedAt = supabaseItem.updatedAt instanceof Date
          ? supabaseItem.updatedAt.getTime()
          : new Date(supabaseItem.updatedAt).getTime();
        
        if (localUpdatedAt > supabaseUpdatedAt) {
          console.log(`[ItemsStore] Keeping local version of ${localItem.id} (local is newer)`);
          return localItem;
        }
        
        // When Supabase is newer or equal, keep the DB row and only preserve safe in-memory fields.
        return {
          ...supabaseItem,
          exit_status: (supabaseItem as any).exit_status ?? (localItem as any).exit_status ?? 'active',
          duplicateOfId: localItem.duplicateOfId,
          mergedFromIds: localItem.mergedFromIds,
          splitFromId: localItem.splitFromId,
          childItemIds: localItem.childItemIds,
          // `queue_id` is a real column, not in-memory state — it was grouped with
          // the local-only fields above, so the server value was discarded on every
          // load and the Item List's Queue column rendered blank for any queue set
          // anywhere but this browser. Server wins; local only covers a row created
          // offline that hasn't synced yet.
          queueId: supabaseItem.queueId ?? localItem.queueId,
          hasPendingClarifications: localItem.hasPendingClarifications,
          // Not part of the canonical Item shape (see lib/itemReadiness.ts
          // getAttributeValues, which reads this the same defensive way).
          attributeValues: (localItem as { attributeValues?: unknown }).attributeValues,
        };
      });
      
      // Stage 7 Slice 6: Do not resurrect local-only items — only DB-backed data (no false reality).
      set({ 
        items: mergedFromSupabase,
        itemsFetchWorkspaceId: getCurrentWorkspaceId(),
        syncState: { 
          ...get().syncState, 
          isLoading: false, 
          lastSyncAt: new Date(),
          syncError: null 
        }
      });
      console.log(`[ItemsStore] Loaded ${migrated.length} from Supabase (DB-only; no local-only merge).`);
    } else {
      set({
        syncState: {
          ...get().syncState,
          isLoading: false,
          syncError: null,
        },
      });
    }
  },
  
  syncToSupabase: async (item: Item) => {
    if (!isSupabaseAvailable()) {
      return { success: true }; // Silently succeed if Supabase not available
    }
    
    set({ syncState: { ...get().syncState, isSyncing: true } });
    
    // Only sync schema-compatible fields
    const supabaseFields = getSupabaseCompatibleFields(item);
    const result = await updateRecord<Record<string, unknown>>(TABLE_NAME, item.id, supabaseFields);
    
    set({ syncState: { ...get().syncState, isSyncing: false } });
    
    if (result.error) {
      console.error('[ItemsStore] Failed to sync to Supabase:', result.error);
      return { success: false, error: result.error };
    }
    
    return { success: true };
  },
  
  // ========================================================================
  // CRUD Actions (with Supabase sync)
  // ========================================================================
  
  createItem: async (itemData) => {
    const result = await get().createItemWithReason(itemData);
    return result.ok;
  },

  createItemWithReason: async (itemData) => {
    const workspaceId = getCurrentWorkspaceId();
    const queueId =
      itemData.queueId === null
        ? undefined // caller explicitly asked for "no queue"
        : itemData.queueId ??
          (workspaceId ? getRootQueueIdForWorkspace(workspaceId) ?? undefined : undefined);

    const userId = useUserStore.getState().userId;
    // Stamp the item with the active collaboration space unless the caller
    // already provided one. NULL means org-level (visible in all spaces).
    //
    // Only stamp a space the loaded list still vouches for. currentSpaceId is
    // persisted to localStorage, so a space that was deleted — or replaced by a
    // workspace reseed, which mints fresh UUIDs — leaves a dangling id behind
    // that outlives the reload. Sending it fails items_space_id_fkey on every
    // insert. Falling back to NULL (org-level) degrades where the old behaviour
    // hard-failed.
    // Gated on hasFetched so a create racing the initial load is not stripped
    // of a selection that is actually fine. A stale list can still vouch for a
    // dead id, so this is the cheap guard, not the guarantee — the insert below
    // recovers from the FK violation itself.
    const spaceStore = useSpaceStore.getState();
    const selectedSpaceId = spaceStore.currentSpaceId ?? null;
    const spaceLooksDead =
      spaceStore.hasFetched &&
      selectedSpaceId !== null &&
      !spaceStore.mySpaces.some(s => s.id === selectedSpaceId);
    const spaceId = itemData.spaceId !== undefined
      ? itemData.spaceId
      : (spaceLooksDead ? null : selectedSpaceId);

    const { queueId: _requestedQueueId, ...itemDataWithoutQueue } = itemData;
    const newItem: Item = {
      ...itemDataWithoutQueue,
      id: itemData.id || generateUUID(),
      lifecycle_state: 'item',
      exit_status: (itemData as any).exit_status ?? 'active',
      lifecycle_stage: (itemData as any).lifecycle_stage ?? 'intake', // 'item' = bypasses triage
      createdAt: new Date(),
      updatedAt: new Date(),
      tags: itemData.tags || [],
      spaceId,
      ...(workspaceId && { workspaceId }),
      ...(queueId && { queueId }),
      ...(userId && {
        createdBy: userId,
        created_by_user_id: userId,
      } as Partial<Item>),
    };

    // A click soon after a page load can be answered before this person's rights
    // have arrived; the shared check loads them and asks again before saying no.
    const policyDecision = await askAgainAfterPermissionsLoad(() =>
      checkPolicyForItem('items.create', newItem.id, newItem as unknown as Record<string, unknown>),
    );
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemsStore] Policy denied for items.create:`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'items', action: 'createItem', message: `Cannot add: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return { ok: false, reason: getReasonMessage(reasonCode) };
    }

    set((state) => ({ items: [...state.items, newItem] }));

    // The item_created audit row is written by the database in the insert's
    // own transaction (migration 761 trg_audit_item_created) — not here, where
    // it used to land before the insert and outlive a failed one.

    if (!isSupabaseAvailable()) return { ok: true, id: newItem.id };

    const supabaseFields = getSupabaseCompatibleFields(newItem);
    console.log('[ItemsStore] Inserting to Supabase:', supabaseFields);

    let result = await insertRecord<Record<string, unknown>>(TABLE_NAME, supabaseFields);

    // The selected space was deleted (or replaced by a reseed) while this client
    // still held its id. Drop the dead selection so the rest of the session — and
    // the next reload, since currentSpaceId is persisted — stops sending it, then
    // retry this item at org level rather than losing it.
    if (result.error && isDeadSpaceReference(result.error) && supabaseFields['space_id']) {
      console.warn('[ItemsStore] space_id no longer exists — clearing selection and retrying org-level');
      useSpaceStore.getState().setCurrentSpace(null);
      supabaseFields['space_id'] = null;
      newItem.spaceId = null;
      result = await insertRecord<Record<string, unknown>>(TABLE_NAME, supabaseFields);
    }

    if (result.error) {
      console.error('[ItemsStore] Insert failed:', result.error);
      const reason = humanizeSyncError(String(result.error));
      // The row was shown optimistically above. It was never saved, so take it
      // back out — otherwise the list shows an item that vanishes on reload.
      set((state) => ({ items: state.items.filter((i) => i.id !== newItem.id) }));
      window.dispatchEvent(new CustomEvent('store-warning', {
        detail: {
          store: 'items',
          action: 'createItem',
          message: reason
        }
      }));
      return { ok: false, reason };
    }

    console.log('[ItemsStore] Insert successful');

    // Template agents (the Air Traffic Controller, the Value Estimator and custom agents) run on new items
    // on the server: the item_created event (agent_events) wakes the ambient
    // event_watch job for each agent with that event switched on (spec §4).
    return { ok: true, id: newItem.id };
  },

  splitItem: async (sourceId, childTitles, rationale) => {
    const source = get().items.find(i => i.id === sourceId);
    if (!source) return { success: false, childIds: [], error: 'Source item not found' };
    if (childTitles.length < 2) return { success: false, childIds: [], error: 'Split requires at least two child items' };

    const userId = useUserStore.getState().userId || 'unknown';
    const now = new Date();

    const childItems: Item[] = childTitles.map(title => ({
      ...source,
      id: generateUUID(),
      title,
      description: source.description || '',
      createdAt: now,
      updatedAt: now,
      lifecycle_state: 'item' as const,
      exit_status: 'active' as const,
      lifecycle_stage: 'intake' as const,
      dedupeStatus: 'pending' as const,
      splitFromId: sourceId,
      parentItemId: undefined,
      duplicateOfId: undefined,
      mergedFromIds: undefined,
      childItemIds: undefined,
      shapingOwnerId: undefined,
      shapingStartedAt: undefined,
      shapedAt: undefined,
      readyForRanking: false,
      shapingCompleteness: 0,
      createdBy: userId,
      created_by_user_id: userId,
      tags: [...(source.tags || [])],
    }));

    const childIds = childItems.map(c => c.id);
    const lineageEventId = generateUUID();

    // ================================================================
    // DURABLE SPLIT: lineage is written FIRST and is REQUIRED.
    // If lineage cannot be recorded, the split does not happen.
    // Order: lineage → children → source update → in-memory → audit
    // ================================================================

    if (isSupabaseAvailable()) {
      // Phase 1: Write lineage event FIRST — this is the authoritative record
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        return { success: false, childIds: [], error: 'Database connection unavailable' };
      }

      const { error: lineageError } = await supabase.from('lineage_events').insert({
        id: lineageEventId,
        event_type: 'split',
        source_item_ids: [sourceId],
        target_item_ids: childIds,
        performed_by: userId,
        rationale: rationale || null,
        created_at: now.toISOString(),
        workspace_id: getCurrentWorkspaceId() ?? null,
      });

      if (lineageError) {
        console.error('[ItemsStore] Split aborted — lineage write failed:', lineageError.message);
        return { success: false, childIds: [], error: `Split failed: could not record lineage (${lineageError.message})` };
      }

      // Phase 2: Create child items — roll back lineage if this fails
      const persistedIds: string[] = [];
      let childCreationError: string | null = null;

      for (const child of childItems) {
        const dbFields = getSupabaseCompatibleFields(child);
        const result = await insertRecord<Record<string, unknown>>(TABLE_NAME, dbFields);
        if (result.error) {
          childCreationError = `Failed to create child "${child.title}": ${result.error}`;
          break;
        }
        persistedIds.push(child.id);
      }

      if (childCreationError) {
        // Roll back: delete any children that were created
        for (const rollbackId of persistedIds) {
          await deleteRecord(TABLE_NAME, rollbackId);
        }
        // Roll back: delete the lineage event
        await supabase.from('lineage_events').delete().eq('id', lineageEventId);
        return { success: false, childIds: [], error: childCreationError };
      }

      // Phase 3: Mark source as split
      const sourceUpdateResult = await updateRecord(TABLE_NAME, sourceId, {
        dedupe_status: 'split',
        updated_at: now.toISOString(),
      });
      if (sourceUpdateResult.error) {
        console.error('[ItemsStore] Failed to mark source as split (non-fatal):', sourceUpdateResult.error);
      }
    } else {
      // Demo / offline mode: no Supabase, no durable lineage possible
      console.warn('[ItemsStore] Split in demo mode — no durable lineage');
    }

    // Phase 4: Commit to in-memory store
    const sourceUpdates: Partial<Item> = {
      dedupeStatus: 'split' as any,
      updatedAt: now,
    };

    set((state) => ({
      items: [
        ...state.items.map(i => i.id === sourceId ? { ...i, ...sourceUpdates } : i),
        ...childItems,
      ],
    }));

    // Phase 5: Audit event (best-effort — lineage is already durable)
    try {
      useAuditStore.getState().logShapingEvent({
        type: 'item_split',
        itemId: sourceId,
        actorId: userId,
        actorRole: 'shaper',
        details: { targetItemIds: childIds, rationale, lineageEventId },
      });
    } catch (_) { /* audit is best-effort; lineage_events is the authority */ }

    return { success: true, childIds };
  },

  createLineageEvent: async (params) => {
    const { eventType, sourceItemIds, targetItemIds, rationale } = params;
    const userId = useUserStore.getState().userId || 'unknown';
    const now = new Date();
    const lineageEventId = generateUUID();

    if (!isSupabaseAvailable()) {
      return { success: false, error: 'Database connection unavailable' };
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Database connection unavailable' };

    const { error: lineageError } = await supabase.from('lineage_events').insert({
      id: lineageEventId,
      event_type: eventType,
      source_item_ids: sourceItemIds,
      target_item_ids: targetItemIds,
      performed_by: userId,
      rationale: rationale || null,
      created_at: now.toISOString(),
      workspace_id: getCurrentWorkspaceId() ?? null,
    });

    if (lineageError) {
      console.error('[ItemsStore] createLineageEvent failed:', lineageError.message);
      return { success: false, error: lineageError.message };
    }
    return { success: true, lineageEventId };
  },

  retireItem: async (itemId, rationale) => {
    const item = get().items.find((i) => i.id === itemId);
    if (!item) return { success: false, error: 'Item not found' };

    const result = await get().createLineageEvent({
      eventType: 'retire',
      sourceItemIds: [itemId],
      targetItemIds: [],
      rationale,
    });
    if (!result.success) return result;

    get().updateItem(itemId, {
      exit_status: 'archived' as any,
      updatedAt: new Date(),
    });

    return { success: true };
  },

  supersedeItem: async (sourceItemId, targetItemId, rationale) => {
    const source = get().items.find((i) => i.id === sourceItemId);
    if (!source) return { success: false, error: 'Source item not found' };

    const result = await get().createLineageEvent({
      eventType: 'supersede',
      sourceItemIds: [sourceItemId],
      targetItemIds: [targetItemId],
      rationale,
    });
    if (!result.success) return result;

    get().updateItem(sourceItemId, {
      exit_status: 'archived' as any,
      duplicateOfId: targetItemId,
      dedupeStatus: 'duplicate_of' as any,
      updatedAt: new Date(),
    });

    return { success: true };
  },

  forkItem: async (sourceId, forkTitle, rationale) => {
    const source = get().items.find((i) => i.id === sourceId);
    if (!source) return { success: false, error: 'Source item not found' };

    const userId = useUserStore.getState().userId || 'unknown';
    const now = new Date();
    const forkId = generateUUID();
    const lineageEventId = generateUUID();
    const workspaceId = getCurrentWorkspaceId();

    const forkItem: Item = {
      ...source,
      id: forkId,
      title: forkTitle,
      description: source.description || '',
      lifecycle_state: 'item' as const,
      exit_status: 'active' as const,
      lifecycle_stage: 'shaping' as const,
      createdAt: now,
      updatedAt: now,
      tags: [...(source.tags || [])],
      source: source.source ?? 'fork',
      itemCategory: source.itemCategory,
      dedupeStatus: 'pending' as const,
      splitFromId: undefined,
      parentItemId: undefined,
      duplicateOfId: undefined,
      readyForRanking: false,
      shapingCompleteness: 0,
      createdBy: userId,
      created_by_user_id: userId,
      ...(workspaceId && { workspaceId }),
    };

    // Durable write ordering: lineage first, then item
    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return { success: false, error: 'Database connection unavailable' };

      // Phase 1: Write lineage event
      const { error: lineageError } = await supabase.from('lineage_events').insert({
        id: lineageEventId,
        event_type: 'fork',
        source_item_ids: [sourceId],
        target_item_ids: [forkId],
        performed_by: userId,
        rationale: rationale || null,
        created_at: now.toISOString(),
        workspace_id: getCurrentWorkspaceId() ?? null,
      });
      if (lineageError) {
        return { success: false, error: `Fork failed: could not record lineage (${lineageError.message})` };
      }

      // Phase 2: Create fork item — roll back lineage if this fails
      const dbFields = getSupabaseCompatibleFields(forkItem);
      const result = await insertRecord<Record<string, unknown>>(TABLE_NAME, dbFields);
      if (result.error) {
        await supabase.from('lineage_events').delete().eq('id', lineageEventId);
        return { success: false, error: `Failed to create fork: ${result.error}` };
      }
    }

    // Commit to in-memory store (source stays active for forks)
    set((state) => ({ items: [forkItem as Candidate, ...state.items] }));

    return { success: true, forkId };
  },

  mergeItems: async (sourceIds, mergedTitle, rationale) => {
    if (sourceIds.length < 2) return { success: false, error: 'Need at least two items to merge' };
    const sources = sourceIds.map((id) => get().items.find((i) => i.id === id)).filter(Boolean) as Candidate[];
    if (sources.length !== sourceIds.length) return { success: false, error: 'One or more items not found' };

    const userId = useUserStore.getState().userId || 'unknown';
    const now = new Date();
    const mergedId = generateUUID();
    const lineageEventId = generateUUID();
    const workspaceId = getCurrentWorkspaceId();
    const allTags = [...new Set(sources.flatMap((s) => s.tags || []))];
    const firstSource = sources[0];

    const mergedItem: Item = {
      id: mergedId,
      title: mergedTitle,
      description: sources.map((s) => s.description).filter(Boolean).join('\n\n---\n\n'),
      lifecycle_state: 'item' as const,
      exit_status: 'active' as const,
      lifecycle_stage: 'shaping' as const,
      createdAt: now,
      updatedAt: now,
      tags: allTags,
      source: firstSource.source ?? 'merge',
      itemCategory: firstSource.itemCategory,
      dedupeStatus: 'pending' as const,
      readyForRanking: false,
      shapingCompleteness: 0,
      createdBy: userId,
      created_by_user_id: userId,
      ...(workspaceId && { workspaceId }),
      ...(firstSource.queueId && { queueId: firstSource.queueId }),
    };

    // Durable write ordering: lineage first, then item, then archive sources
    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return { success: false, error: 'Database connection unavailable' };

      // Phase 1: Write lineage event
      const { error: lineageError } = await supabase.from('lineage_events').insert({
        id: lineageEventId,
        event_type: 'merge',
        source_item_ids: sourceIds,
        target_item_ids: [mergedId],
        performed_by: userId,
        rationale: rationale || null,
        workspace_id: workspaceId ?? null,
        created_at: now.toISOString(),
      });
      if (lineageError) {
        return { success: false, error: `Merge failed: could not record lineage (${lineageError.message})` };
      }

      // Phase 2: Create merged item — roll back lineage if this fails
      const dbFields = getSupabaseCompatibleFields(mergedItem);
      const result = await insertRecord<Record<string, unknown>>(TABLE_NAME, dbFields);
      if (result.error) {
        await supabase.from('lineage_events').delete().eq('id', lineageEventId);
        return { success: false, error: `Failed to create merged item: ${result.error}` };
      }

      // Phase 3: Archive source items
      for (const srcId of sourceIds) {
        await updateRecord(TABLE_NAME, srcId, {
          exit_status: 'archived',
          updated_at: now.toISOString(),
        });
      }
    }

    // Commit to in-memory store
    set((state) => ({
      items: [
        ...state.items.map((i) => sourceIds.includes(i.id) ? { ...i, exit_status: 'archived' as any, updatedAt: now } : i),
        mergedItem as Candidate,
      ],
    }));

    return { success: true, mergedId };
  },

  addCandidate: (data) => {
    const workspaceId = getCurrentWorkspaceId();
    const queueId =
      workspaceId ? getRootQueueIdForWorkspace(workspaceId) ?? undefined : undefined;
    const userId = useUserStore.getState().userId;
    const createdBy = data.submittedBy ?? data.createdBy ?? userId ?? 'system';

    const newItem: Item = {
      id: generateUUID(),
      title: data.title,
      description: data.description ?? '',
      lifecycle_state: 'item',
      exit_status: 'active' as const,
      lifecycle_stage: 'intake',
      createdAt: new Date(),
      updatedAt: new Date(),
      tags: [],
      source: data.source ?? 'ost',
      ...(workspaceId && { workspaceId }),
      ...(queueId && { queueId }),
      createdBy,
      ...(userId && { created_by_user_id: userId } as Partial<Item>),
      ...(data.ostNodeId && { ostNodeId: data.ostNodeId }),
      ...(data.ostOpportunityId && { ostOpportunityId: data.ostOpportunityId }),
      ...(data.metadata && { metadata: data.metadata } as Partial<Item>),
    };

    const policyDecision = checkPolicyForItem('items.create', newItem.id, newItem as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn('[ItemsStore] Policy denied for items.create (addCandidate):', reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'items', action: 'addCandidate', message: `Cannot add candidate: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return undefined;
    }

    set((state) => ({ items: [...state.items, newItem] }));

    // item_created audit row: written by the database with the insert (761).

    if (isSupabaseAvailable()) {
      const supabaseFields = getSupabaseCompatibleFields(newItem);
      insertRecord<Record<string, unknown>>(TABLE_NAME, supabaseFields).then(result => {
        if (result.error) {
          console.error('[ItemsStore] addCandidate insert failed:', result.error);
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: { store: 'items', action: 'addCandidate', message: `Cloud sync failed: ${result.error}` },
          }));
        }
      });
    }
    return newItem;
  },

  updateItem: async (id, updates, options) => {
    if (!id) {
      console.error('[ItemsStore] updateItem: itemId is required');
      return { ok: false, reason: 'No item id' };
    }
    const item = get().items.find(c => c.id === id);
    if (!item) {
      console.error('[ItemsStore] Item not found:', id);
      window.dispatchEvent(new CustomEvent('store-error', { 
        detail: { store: 'items', action: 'updateItem', message: `Item not found: ${id}` } 
      }));
      return { ok: false, reason: 'Item not found' };
    }
    
    // F02: block workspace reassignment while item is in an active session pool. Canon §F02.
    // workspace_id is already stripped from DB writes (line below), but we catch the attempt
    // early and surface a clear error rather than silently ignoring it.
    const incomingWorkspaceId = (updates as Partial<Item & { workspaceId?: string }>).workspaceId;
    if (incomingWorkspaceId && incomingWorkspaceId !== item.workspaceId) {
      console.warn('[ItemsStore] F02: workspace reassignment attempted on item', id, '— blocked. Use moveItemToWorkspace() after removing from active sessions.');
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: {
          store: 'items',
          action: 'updateItem',
          message: 'Cannot reassign this item to a different workspace via updateItem. Use moveItemToWorkspace() — it enforces the F02 session-pool check first.',
          code: 'F02_USE_DEDICATED_MOVE_ACTION',
        },
      }));
      return { ok: false, reason: 'An item cannot change workspace this way' };
    }

    // Deferring stamps when it happened. Every defer in the UI (row action,
    // bulk status change, item detail) funnels through here, so the stamp is
    // set once rather than remembered by each caller.
    if ((updates as Partial<Item>).exit_status === 'deferred' && item.exit_status !== 'deferred'
        && !('deferredAt' in updates)) {
      (updates as Partial<Item>).deferredAt = new Date();
    }

    // Moving an item into a queue adopts that queue's default owner — but only
    // when the item has no owner yet. Placed here rather than in each caller
    // because every queue change in the UI (item detail chip, quick panel, bulk
    // move, triage redirect) funnels through updateItem; wiring callers one by
    // one would mean the next new one silently skips the rule.
    const incomingQueueId = (updates as Partial<Item & { queueId?: string }>).queueId;
    if (incomingQueueId && incomingQueueId !== item.queueId) {
      const adopted = resolveOwnerOnQueueMove(incomingQueueId, item.ownerId);
      if (adopted) {
        (updates as Partial<Item>).ownerId = adopted;
      }
    }

    // Policy check for content edits
    // Determine if this is a content edit vs. a status/metadata edit
    const isContentEdit = 'title' in updates || 'description' in updates;
    const action = isContentEdit ? 'items.edit.content' : 'items.edit';
    const evalContext = options?.phase ? { phase: options.phase } : undefined;
    const policyDecision = checkPolicyForItem(action, id, item as unknown as Record<string, unknown>, evalContext);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemsStore] Policy denied for ${action}:`, reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', { 
        detail: { 
          store: 'items', 
          action: 'updateItem', 
          message: `Cannot update: ${reasonCode}`,
          decision: policyDecision,
        } 
      }));
      // Note: Not throwing to maintain backward compatibility
      // In strict mode, uncomment: throw new AuthorizationError(policyDecision, action, 'item', id);
    }
    
    const wasReadyBefore = isItemReadyForPrioritization(item as any);
    const merged = { ...item, ...updates, updatedAt: new Date() } as Item;
    const readinessMirror = computeLegacyReadinessMirror(merged as any);
    const updated = {
      ...merged,
      readyForRanking: readinessMirror.readyForRanking,
    } as Item;
    
    // Update local state first (optimistic update)
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));

    // Comprehensive field-change detection for provenance logging
    const TRACKED_FIELDS = [
      'title', 'description', 'itemCategory', 'altitudeLevel', 'exit_status',
      'dedupeStatus', 'duplicateOfId', 'parentItemId', 'tags', 'desiredOutcome',
      'acceptanceCriteria', 'readyForRanking', 'source', 'orgUnitId', 'queueId', 'productNodeId',
      'lifecycle_state', 'altitudeLifecycleState', 'leadershipApproved', 'attributes',
      'classificationStatus', 'work_type', 'launch_type', 'investment_type', 'external_id', 'metadata',
      'evidenceOfSuccess', 'whyNow',
      // How sure we are, and effort — changed on the item page, never recorded before (2026-10-15).
      'planning_maturity', 'knowledge_status', 'confidence_level', 'effortCapacityUnits', 'effortSource',
    ] as const;
    const fieldChanges: Record<string, { from: unknown; to: unknown }> = {};
    for (const field of TRACKED_FIELDS) {
      const oldVal = (item as unknown as Record<string, unknown>)[field];
      const newVal = (updated as unknown as Record<string, unknown>)[field];
      if (oldVal !== newVal && field in updates) {
        fieldChanges[field] = { from: oldVal, to: newVal };
      }
    }

    // History is written only after the save succeeds, so a refused save leaves no
    // "changed" line behind (2026-10-15; it used to be written before the save).
    const recordHistory = () => {
    if (Object.keys(fieldChanges).length > 0) {
      // Unified attributes (doc 07): a canonical change may need to travel to
      // every bound app. Queued, never executed here; the service decides
      // whether any declaration cares. Dynamic import avoids a store↔service
      // cycle and costs nothing when no attribute is unified.
      import('../../lib/semanticConnectors/unifiedAttributesService')
        .then(({ onItemFieldsChanged }) =>
          onItemFieldsChanged({
            workspaceId: updated.workspaceId ?? updated.workspace_id ?? null,
            itemId: id,
            fieldChanges,
          }),
        )
        .catch(() => {});
      try {
        const auditStore = useAuditStore.getState();
        const userId = useUserStore.getState().userId || 'unknown';

        if (fieldChanges.exit_status) {
          auditStore.logEvent({
            type: 'item_transition',
            itemId: id,
            itemTitle: updated.title,
            fromState: String(fieldChanges.exit_status.from || 'active'),
            toState: String(fieldChanges.exit_status.to),
            action: 'exit_status_change',
            actorId: userId,
            actorRole: 'editor',
            metadata: fieldChanges,
          } as any);

          // Fire state-change notification to relevant stakeholders
          const stateRecipients = [
            (item as any).createdBy,
            (item as any).createdByUserId,
          ].filter((uid): uid is string => !!uid && uid !== userId);
          if (stateRecipients.length > 0) {
            import('../../lib/notificationService').then(({ notifyItemStateChanged }) => {
              notifyItemStateChanged({
                itemId: id,
                itemTitle: updated.title ?? 'Untitled',
                fromState: String(fieldChanges.exit_status!.from || 'active'),
                toState: String(fieldChanges.exit_status!.to),
                recipientUserIds: [...new Set(stateRecipients)],
              }).catch(() => {});
            }).catch(() => {});
          }
        }


        for (const [field, change] of Object.entries(fieldChanges)) {
          if (field === 'exit_status') continue;
          const eventType = itemFieldEventType(field);
          let details: Record<string, unknown> = { field, from: change.from, to: change.to };
          if (field === 'attributes') {
            const single = getSingleAttributeChange(change.from, change.to);
            if (single) {
              details = { field: 'attributes', attributeKey: single.attributeKey, from: single.from, to: single.to };
            }
          } else if (field === 'metadata') {
            const single = getSingleMetadataChange(change.from, change.to);
            if (single) {
              details = { field: 'metadata', attributeKey: single.attributeKey, from: single.from, to: single.to };
            }
          }
          auditStore.logShapingEvent({
            type: eventType as any,
            itemId: id,
            actorId: userId,
            actorRole: 'editor',
            details,
          });
        }
      } catch (e) {
        console.warn('[ItemsStore] Failed to log provenance:', e);
      }
    }
    };

    // Sync to Supabase
    if (!isSupabaseAvailable()) {
      recordHistory();
      return { ok: true };
    }
    {
      const supabaseFields = getSupabaseCompatibleFields(updated);
      // Never send workspace_id on update (we are not moving items between workspaces). Only log if item has no workspace (canonical: workspaceId).
      const itemWorkspaceId = updated.workspaceId ?? (updated as { workspace_id?: string }).workspace_id;
      if (!itemWorkspaceId) {
        console.error('[ItemsStore] Item missing workspaceId; omitting workspace_id from update. Row id:', id);
      }
      delete supabaseFields.workspace_id;
      // Only send exit_status when it is explicitly being changed. Sending it on every
      // field update causes PostgREST schema-cache errors on instances where the schema
      // cache has not yet refreshed after the migration that added the column.
      if (!Object.prototype.hasOwnProperty.call(updates, 'exit_status')) {
        delete supabaseFields.exit_status;
      }
      console.log('[ItemsStore] Updating in Supabase:', { id, fields: Object.keys(supabaseFields) });
      
      const result = await updateRecord<Record<string, unknown>>(TABLE_NAME, id, supabaseFields);
      {
        if (result.error) {
          console.error('[ItemsStore] Update failed:', result.error);
          window.dispatchEvent(new CustomEvent('store-warning', { 
            detail: { 
              store: 'items', 
              action: 'updateItem', 
              message: `Cloud sync failed: ${result.error}` 
            } 
          }));
          return { ok: false, reason: result.error };
        } else {
          console.log('[ItemsStore] Update successful');
          recordHistory();
          if (!wasReadyBefore && readinessMirror.readyForRanking) {
            syncSubmissionReadyStatus(id);
            // Notify stakeholders that item is now ready for ranking
            const readyRecipients = [
              (item as any).createdBy,
              (item as any).createdByUserId,
              (item as any).shapingOwnerId,
            ].filter((uid): uid is string => !!uid);
            if (readyRecipients.length > 0) {
              import('../../lib/notificationService').then(({ notifyItemReadyForRanking }) => {
                notifyItemReadyForRanking({
                  itemId: id,
                  itemTitle: updated.title ?? 'Untitled',
                  recipientUserIds: [...new Set(readyRecipients)],
                }).catch(() => {});
              }).catch(() => {});
            }
          }
        }
      }
    }
    return { ok: true };
  },
  
  // ── F02: workspace move with session-pool check ────────────────────────────
  moveItemToWorkspace: async (itemId, targetWorkspaceId, movedBy) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item) return { success: false, error: `Item not found: ${itemId}` };
    if (item.workspaceId === targetWorkspaceId) return { success: true }; // no-op

    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase not available' };

    // F02: check for active session pool membership before allowing the move.
    const { data: activeSessions, error: checkErr } = await supabase
      .from('prioritization_sessions')
      .select('id, name')
      .contains('item_ids', [itemId])
      .not('state', 'eq', 'PUBLISHED')
      .limit(5);

    if (checkErr) {
      console.warn('[ItemsStore] F02: could not check session pool membership:', checkErr.message);
      return { success: false, error: 'Could not verify session pool membership. Try again.' };
    }

    if (activeSessions && activeSessions.length > 0) {
      const names = activeSessions.map((s: { id: string; name?: string }) => s.name ?? s.id).join(', ');
      console.warn('[ItemsStore] F02: workspace move blocked — active session(s):', names);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: {
          store: 'items',
          action: 'moveItemToWorkspace',
          message: `Cannot move item to a different workspace — it is in active session pool(s): ${names}. Remove it from those sessions first.`,
          code: 'F02_SESSION_POOL_LOCK',
          sessions: activeSessions,
        },
      }));
      return { success: false, error: `Item is in ${activeSessions.length} active session pool(s). Remove it from those sessions before moving workspaces.` };
    }

    // Safe to move: update workspace_id directly (this is the only sanctioned path).
    const { error: updateErr } = await supabase
      .from('items')
      .update({ workspace_id: targetWorkspaceId, updated_at: new Date().toISOString() })
      .eq('id', itemId);

    if (updateErr) {
      console.error('[ItemsStore] F02: workspace move DB error:', updateErr.message);
      return { success: false, error: updateErr.message };
    }

    // Reflect in local state
    set(state => ({
      items: state.items.map(c =>
        c.id === itemId ? { ...c, workspaceId: targetWorkspaceId, updatedAt: new Date() } : c
      ),
    }));

    console.log(`[ItemsStore] F02: item ${itemId} moved to workspace ${targetWorkspaceId} by ${movedBy}`);
    return { success: true };
  },

  retireItems: async (ids, reason) => {
    const retired: string[] = [];
    const failed: RetireItemFailure[] = [];
    for (const id of ids) {
      const title = get().items.find(c => c.id === id)?.title;
      const result = await get().retireItemWithReason(id, reason);
      if (result.ok) retired.push(id);
      else failed.push({ id, title, reason: result.reason ?? 'Retire failed', blockedBy: result.blockedBy, remedy: result.remedy });
    }
    return { retired, failed };
  },

  retireItemWithReason: async (id, reason) => {
    // No client-side hard delete for items any more (owner ruling 2026-09-22).
    // Authority (creator or workspace admin) and the "already in use" check
    // both live in retire_new_item; the server is the only judge.
    if (!reason || !reason.trim()) {
      return { ok: false, reason: 'A written reason is required to retire an item.' };
    }
    if (!isSupabaseAvailable()) {
      return { ok: false, reason: 'Not connected to the database' };
    }

    let result: ReversalResult;
    try {
      result = await retireNewItem(id, reason);
    } catch (err) {
      return { ok: false, reason: err instanceof Error ? err.message : 'Retire failed' };
    }

    if (!result.ok) {
      return {
        ok: false,
        reason: result.error ?? 'Retire failed',
        blockedBy: result.blockedBy,
        remedy: result.remedy,
      };
    }

    // The record stays; it just leaves active consideration.
    set((state) => ({
      items: state.items.map(c =>
        c.id === id ? ({ ...c, exit_status: 'archived', updatedAt: new Date() } as typeof c) : c,
      ),
    }));
    return { ok: true };
  },
  
  promoteToItem: (id, attributes) => {
    mustHave('items.edit');

    const existing = get().items.find(c => c.id === id);
    if (!existing) {
      console.error('Cannot promote: item not found');
      return;
    }

    const policyDecision = checkPolicyForItem('items.edit', id, existing as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemsStore] Policy denied for items.edit (promote):`, reasonCode);
      window.dispatchEvent(new CustomEvent('authorization-denied', {
        detail: {
          store: 'items',
          action: 'promoteToItem',
          message: `Cannot promote: ${reasonCode}`,
          decision: policyDecision,
        }
      }));
      return;
    }

    const shapedItem: ShapedItem = {
      ...existing,
      lifecycle_state: 'item',
      exit_status: (existing as any).exit_status ?? 'active',
      lifecycle_stage: 'shaping',
      attributes,
      shapingCompleteness: 0,
      readyForRanking: false,
      updatedAt: new Date()
    };
    
    // Update local state first (optimistic update)
    set((state) => ({
      items: state.items.map(c => c.id === id ? shapedItem : c)
    }));
    
    // Then sync to Supabase
    if (isSupabaseAvailable()) {
      updateRecord<ShapedItem>(TABLE_NAME, id, shapedItem).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to promote to item in Supabase:', result.error);
          dispatchStoreError('Items', 'promote to item', result.error);
        }
      });
    }
  },
  
  promoteToPriority: (id, itemPoolId, rank) => {
    const existing = get().items.find(c => c.id === id);
    if (!existing) {
      console.error('Cannot promote: item not found');
      return;
    }

    const policyDecision = checkPolicyForItem('items.edit', id, existing as unknown as Record<string, unknown>);
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
      console.warn(`[ItemsStore] Policy denied for items.edit (ranking):`, reasonCode);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authorization-denied', {
          detail: { store: 'items', action: 'promoteToPriority', message: `Cannot promote: ${reasonCode}`, decision: policyDecision },
        }));
      }
      return;
    }

    const shaped = existing as ShapedItem;
    const updated: Priority = {
      ...shaped,
      lifecycle_state: 'item',
      exit_status: (existing as any).exit_status ?? 'active',
      lifecycle_stage: 'ranking',
      itemPoolId,
      rank,
      aboveTheLine: false,
      updatedAt: new Date()
    };

    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));

    if (isSupabaseAvailable()) {
      updateRecord<Priority>(TABLE_NAME, id, updated).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to promote to priority in Supabase:', result.error);
          dispatchStoreError('Items', 'promote to priority', result.error);
        }
      });
    }
  },

  promoteSessionItems: async (sessionId, itemIds, promotedBy) => {
    const result = await promoteSessionItemsLib(sessionId, itemIds, promotedBy);
    if (result.success && result.itemCount && result.itemCount > 0) {
      await get().fetchFromSupabase();
    }
    return result;
  },
  
  leadershipApprovePriority: (id, approvedBy) => {
    const item = get().items.find(c => c.id === id);
    if (!item) {
      console.error('[ItemsStore] Cannot approve: item not found');
      return;
    }
    
    const updated = {
      ...item,
      leadershipApproved: true,
      leadershipApprovedAt: new Date(),
      leadershipApprovedBy: approvedBy,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      updateRecord<Item>(TABLE_NAME, id, updated).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to approve priority in Supabase:', result.error);
          dispatchStoreError('Items', 'leadership approve', result.error);
        }
      });
    }
  },
  
  revokeLeadershipApproval: (id) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return;
    
    const updated = {
      ...item,
      leadershipApproved: false,
      leadershipApprovedAt: undefined,
      leadershipApprovedBy: undefined,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      updateRecord<Item>(TABLE_NAME, id, updated).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to revoke approval in Supabase:', result.error);
          dispatchStoreError('Items', 'revoke approval', result.error);
        }
      });
    }
  },
  
  updateAttributes: (id, attributes) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return;

    const updated = { ...item, attributes, updatedAt: new Date() };
    
    // Update local state first (optimistic update)
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));
    
    // Then sync to Supabase
    if (isSupabaseAvailable()) {
      updateRecord<Item>(TABLE_NAME, id, updated).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to update attributes in Supabase:', result.error);
          dispatchStoreError('Items', 'update attributes', result.error);
        }
      });
    }
  },
  
  updateShapingCompleteness: (id, completeness) => {
    // Legacy compatibility entry point: preserve the summary field for older
    // callers, but let updateItem derive readiness from the actual item data.
    get().updateItem(id, { shapingCompleteness: completeness });
  },
  
  assignShapingOwner: async (id, userId) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return { ok: false, reason: 'Item not found' };

    const updated = { ...item, shapingOwnerId: userId, updatedAt: new Date() };

    // Fire assignee notification (non-blocking)
    import('../../lib/notificationService').then(({ notifyStewardAssigned }) => {
      notifyStewardAssigned({
        itemId: id,
        itemTitle: item.title || 'Untitled',
        stewardId: userId,
        stewardName: userId, // will be resolved server-side
        workspaceId: getCurrentWorkspaceId() || undefined,
      }).catch(() => {});
    });

    // Update local state first (optimistic update)
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));

    // Then sync to Supabase
    if (!isSupabaseAvailable()) return { ok: true };
    const result = await updateRecord<Item>(TABLE_NAME, id, updated);
    if (result.error) {
      console.error('[ItemsStore] Failed to assign item owner in Supabase:', result.error);
      dispatchStoreError('Items', 'assign item owner', result.error);
      return { ok: false, reason: result.error };
    }
    return { ok: true };
  },

  assignSteward: (id, userId) => get().assignShapingOwner(id, userId),

  // ========================================================================
  // Ownership Management
  // ========================================================================

  transferOwnership: async (id, newOwnerId) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return { ok: false, reason: 'Item not found' };

    const previous = { ownerId: item.ownerId, delegateId: item.delegateId, ownershipMode: item.ownershipMode };
    const updated: Item = { ...item, ownerId: newOwnerId, delegateId: null, ownershipMode: 'direct', updatedAt: new Date() };

    set((state) => ({ items: state.items.map(c => c.id === id ? updated : c) }));

    if (isSupabaseAvailable()) {
      const result = await updateRecord<Record<string, unknown>>(TABLE_NAME, id, {
        owner_id: newOwnerId,
        delegate_id: null,
        ownership_mode: 'direct',
        updated_at: new Date().toISOString(),
      });
      if (result.error) {
        console.error('[ItemsStore] transferOwnership failed, rolling back:', result.error);
        set((state) => ({
          items: state.items.map(c => c.id === id ? { ...c, ...previous } : c),
        }));
        dispatchStoreError('Items', 'transfer ownership', result.error);
        return { ok: false, reason: result.error };
      }
    }
    if (previous.ownerId !== newOwnerId) recordItemFieldChange(id, 'ownerId', previous.ownerId ?? null, newOwnerId);
    return { ok: true };
  },

  delegateOwnership: async (id, delegateId) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return;

    const previous = { delegateId: item.delegateId, ownershipMode: item.ownershipMode };
    const updated: Item = { ...item, delegateId, ownershipMode: 'delegated', updatedAt: new Date() };

    set((state) => ({ items: state.items.map(c => c.id === id ? updated : c) }));

    if (isSupabaseAvailable()) {
      const result = await updateRecord<Record<string, unknown>>(TABLE_NAME, id, {
        delegate_id: delegateId,
        ownership_mode: 'delegated',
        updated_at: new Date().toISOString(),
      });
      if (result.error) {
        console.error('[ItemsStore] delegateOwnership failed, rolling back:', result.error);
        set((state) => ({
          items: state.items.map(c => c.id === id ? { ...c, ...previous } : c),
        }));
        dispatchStoreError('Items', 'delegate ownership', result.error);
        return;
      }
    }
    if (previous.delegateId !== delegateId) recordItemFieldChange(id, 'delegateId', previous.delegateId ?? null, delegateId);
  },

  reclaimOwnership: async (id) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return;

    const previous = { delegateId: item.delegateId, ownershipMode: item.ownershipMode };
    const updated: Item = { ...item, delegateId: null, ownershipMode: 'direct', updatedAt: new Date() };

    set((state) => ({ items: state.items.map(c => c.id === id ? updated : c) }));

    if (isSupabaseAvailable()) {
      const result = await updateRecord<Record<string, unknown>>(TABLE_NAME, id, {
        delegate_id: null,
        ownership_mode: 'direct',
        updated_at: new Date().toISOString(),
      });
      if (result.error) {
        console.error('[ItemsStore] reclaimOwnership failed, rolling back:', result.error);
        set((state) => ({
          items: state.items.map(c => c.id === id ? { ...c, ...previous } : c),
        }));
        dispatchStoreError('Items', 'reclaim ownership', result.error);
      }
    }
  },

  // ========================================================================
  // Frame Management
  // ========================================================================
  
  addFrame: (itemId, frameData) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item) return '';
    
    const frameId = generateUUID();
    const newFrame: ItemFrame = {
      ...frameData,
      id: frameId,
      sourceItemId: itemId,
      createdAt: new Date(),
    };
    
    const existingFrames = item.frames || [];
    const isFirstFrame = existingFrames.length === 0;
    // Respect the caller's explicit isCanonical=false even on the first frame.
    // Only fall back to "first frame → canonical" when the caller didn't
    // explicitly opt out (i.e. isCanonical is undefined/null/true).
    const shouldBeCanonical = newFrame.isCanonical === false
      ? false
      : isFirstFrame || !!newFrame.isCanonical;

    const updated = {
      ...item,
      frames: [...existingFrames, newFrame],
      canonicalFrameId: shouldBeCanonical ? frameId : item.canonicalFrameId,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      items: state.items.map(c => c.id === itemId ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      const workspaceId = getCurrentWorkspaceId();
      const isPrimary = shouldBeCanonical;

      const doInsert = async () => {
        // Demote existing primary frames BEFORE inserting the new one so the
        // partial unique index idx_item_frames_primary_per_item (item_id WHERE
        // is_primary = true) is never violated. The previous order (insert then
        // demote) raced against a DB that already had a primary frame.
        if (isPrimary) {
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            const { error: demoteErr } = await supabase
              .from(ITEM_FRAMES_TABLE)
              .update({ is_primary: false })
              .eq('item_id', itemId);
            if (demoteErr) {
              console.error('[ItemsStore] Failed to demote existing primary frames:', demoteErr.message);
            }
          }
        }

        const result = await insertRecord(ITEM_FRAMES_TABLE, {
          id: frameId,
          itemId,
          frameType: newFrame.frameType,
          title: newFrame.title,
          description: newFrame.description,
          isPrimary,
          isOriginal: newFrame.isOriginal ?? false,
          createdAt: newFrame.createdAt,
          createdBy: newFrame.createdBy,
          workspaceId: workspaceId ?? item.workspaceId ?? item.workspace_id ?? undefined,
          altitudeScopingInputs: newFrame.altitudeScopingInputs ?? null,
          altitudeLevel: newFrame.altitudeLevel ?? null,
        });
        if (result.error) {
          console.error('[ItemsStore] Failed to add frame in Supabase:', result.error);
          dispatchStoreError('Items', 'add category', result.error);
          return;
        }
        recordItemFieldChange(itemId, 'additionalCategory', null, newFrame.frameType);
      };

      void doInsert();

      if (shouldBeCanonical) {
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          void supabase
            .from(ITEM_FRAMES_TABLE)
            .update({ is_primary: false })
            .eq('item_id', itemId)
            .neq('id', frameId)
            .then(({ error }) => {
              if (error) {
                console.error('[ItemsStore] Failed to clear existing canonical item frames:', error.message);
              }
            });
        }
      }
    }
    
    return frameId;
  },
  
  updateFrame: (itemId, frameId, updates) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item || !item.frames) return;
    
    const updatedFrames = item.frames.map(f =>
      f.id === frameId ? { ...f, ...updates } : f
    );
    
    const updated = { ...item, frames: updatedFrames, updatedAt: new Date() };
    
    set((state) => ({
      items: state.items.map(c => c.id === itemId ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      updateRecord(ITEM_FRAMES_TABLE, frameId, {
        frameType: updates.frameType,
        title: updates.title,
        description: updates.description,
        isOriginal: updates.isOriginal,
        altitudeScopingInputs: updates.altitudeScopingInputs,
        altitudeLevel: updates.altitudeLevel,
      }).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to update frame in Supabase:', result.error);
          dispatchStoreError('Items', 'update category', result.error);
          return;
        }
        const before = item.frames?.find((f) => f.id === frameId);
        recordItemFieldChange(itemId, 'additionalCategory', before ? { type: before.frameType, title: before.title, altitude: before.altitudeLevel ?? null } : null, { type: updates.frameType ?? before?.frameType, title: updates.title ?? before?.title, altitude: updates.altitudeLevel ?? before?.altitudeLevel ?? null });
      });
    }
  },
  
  removeFrame: (itemId, frameId) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item || !item.frames) return;
    
    const updatedFrames = item.frames.filter(f => f.id !== frameId);
    
    // If removing canonical frame, set a new one
    let newCanonicalId = item.canonicalFrameId;
    if (item.canonicalFrameId === frameId) {
      newCanonicalId = updatedFrames[0]?.id;
    }
    
    const updated = {
      ...item,
      frames: updatedFrames,
      canonicalFrameId: newCanonicalId,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      items: state.items.map(c => c.id === itemId ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      deleteRecord(ITEM_FRAMES_TABLE, frameId).then(result => {
        if (result.error || result.data !== true) {
          console.error('[ItemsStore] Failed to remove frame in Supabase:', result.error ?? 'Unknown error');
          dispatchStoreError('Items', 'remove category', result.error ?? 'Unknown error');
          return;
        }
        recordItemFieldChange(itemId, 'additionalCategory', item.frames?.find((f) => f.id === frameId)?.frameType ?? null, null);
      });
      if (newCanonicalId) {
        updateRecord(ITEM_FRAMES_TABLE, newCanonicalId, { isPrimary: true }).then(result => {
          if (result.error) {
            console.error('[ItemsStore] Failed to set replacement canonical frame in Supabase:', result.error);
            dispatchStoreError('Items', 'set active category', result.error);
          }
        });
      }
    }
  },
  
  setCanonicalFrame: (itemId, frameId) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item || !item.frames) return;
    
    // Verify frame exists
    const frameExists = item.frames.some(f => f.id === frameId);
    if (!frameExists) return;
    
    // Update isCanonical flag on all frames
    const updatedFrames = item.frames.map(f => ({
      ...f,
      isCanonical: f.id === frameId,
    }));
    
    const updated = {
      ...item,
      frames: updatedFrames,
      canonicalFrameId: frameId,
      updatedAt: new Date(),
    };
    
    set((state) => ({
      items: state.items.map(c => c.id === itemId ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        void supabase
          .from(ITEM_FRAMES_TABLE)
          .update({ is_primary: false })
          .eq('item_id', itemId)
          .then(({ error }) => {
            if (error) {
              console.error('[ItemsStore] Failed to clear canonical frame flags in Supabase:', error.message);
              dispatchStoreError('Items', 'set active category', error.message);
              return;
            }
            updateRecord(ITEM_FRAMES_TABLE, frameId, { isPrimary: true }).then(result => {
              if (result.error) {
                console.error('[ItemsStore] Failed to set canonical frame in Supabase:', result.error);
                dispatchStoreError('Items', 'set active category', result.error);
                return;
              }
              const it = get().items.find((c) => c.id === itemId);
              recordItemFieldChange(itemId, 'activeCategory', null, it?.frames?.find((f) => f.id === frameId)?.frameType ?? null);
            });
          });
      }
    }
  },

  setUseItemAsCanonical: (itemId) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item) return;
    const updatedFrames = (item.frames || []).map(f => ({ ...f, isCanonical: false }));
    const updated = {
      ...item,
      frames: updatedFrames.length ? updatedFrames : undefined,
      canonicalFrameId: undefined,
      updatedAt: new Date(),
    };
    set((state) => ({
      items: state.items.map(c => c.id === itemId ? updated : c)
    }));
    if (isSupabaseAvailable()) {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        void supabase
          .from(ITEM_FRAMES_TABLE)
          .update({ is_primary: false })
          .eq('item_id', itemId)
          .then(({ error }) => {
            if (error) {
              console.error('[ItemsStore] Failed to clear canonical frame flags in Supabase:', error.message);
              dispatchStoreError('Items', 'clear active category', error.message);
              return;
            }
            recordItemFieldChange(itemId, 'activeCategory', null, item.itemCategory ?? null);
          });
      }
    }
  },
  
  getFramesForItem: (itemId) => {
    const item = get().items.find(c => c.id === itemId);
    // Use stable empty array to avoid creating new references in getters
    return item?.frames ?? EMPTY_FRAMES;
  },
  
  getCanonicalFrame: (itemId) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item?.frames || !item.canonicalFrameId) return undefined;
    return item.frames.find(f => f.id === item.canonicalFrameId);
  },

  findOrCreateFrameForType: (itemId, frameType) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item) return '';

    // Check if a frame of this type already exists
    const existingFrame = (item.frames ?? []).find(f => f.frameType === frameType);
    if (existingFrame) {
      // Just switch to it
      get().setCanonicalFrame(itemId, existingFrame.id);
      return existingFrame.id;
    }

    // Create a new frame of this type — reuse the item's title/description
    const frameId = get().addFrame(itemId, {
      frameType,
      title: item.title,
      description: item.description ?? '',
      createdBy: 'system',
      isCanonical: true,
    });

    return frameId;
  },

  saveFrameAltitude: (itemId, frameId, scopingInputs, altitudeLevel) => {
    const item = get().items.find(c => c.id === itemId);
    if (!item?.frames) return;

    const updatedFrames = item.frames.map(f =>
      f.id === frameId
        ? { ...f, altitudeScopingInputs: scopingInputs, altitudeLevel }
        : f
    );

    set((state) => ({
      items: state.items.map(c =>
        c.id === itemId
          ? { ...c, frames: updatedFrames, updatedAt: new Date() }
          : c
      ),
    }));

    if (isSupabaseAvailable()) {
      updateRecord(ITEM_FRAMES_TABLE, frameId, {
        altitudeScopingInputs: scopingInputs,
        altitudeLevel: altitudeLevel ?? null,
      }).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to save frame altitude in Supabase:', result.error);
          dispatchStoreError('Items', 'save category altitude', result.error);
        }
      });
    }
  },

  // ========================================================================
  // Clarification Helpers
  // ========================================================================
  
  updateHasPendingClarifications: (id, hasPending) => {
    const item = get().items.find(c => c.id === id);
    if (!item) return;
    
    const updated = { ...item, hasPendingClarifications: hasPending, updatedAt: new Date() };
    
    set((state) => ({
      items: state.items.map(c => c.id === id ? updated : c)
    }));
    
    if (isSupabaseAvailable()) {
      updateRecord<Item>(TABLE_NAME, id, updated).then(result => {
        if (result.error) {
          console.error('[ItemsStore] Failed to update clarification status in Supabase:', result.error);
          dispatchStoreError('Items', 'update clarification status', result.error);
        }
      });
    }
  },

  loadItemById: async (id: string) => {
    if (!isSupabaseAvailable()) return;
    // Callers pass raw route params ("/items/intake" matches items/:itemId);
    // items.id and item_frames.item_id are uuid columns, so a non-UUID id can
    // only produce a 22P02 error — skip the round trip entirely.
    if (!isUuid(id)) return;
    const result = await fetchById<Item>(TABLE_NAME, id);
    if (result.error || !result.data) return;
    const supabase = getSafeSupabaseClient();
    let frameRows: ItemFrameRow[] = [];
    if (supabase) {
      const { data: frameData, error: frameError } = await supabase
        .from(ITEM_FRAMES_TABLE)
        .select('*')
        .eq('item_id', id)
        .order('created_at', { ascending: true });
      if (frameError) {
        console.error('[ItemsStore] Failed to fetch item frames for loadItemById:', frameError.message);
      } else {
        frameRows = (frameData as ItemFrameRow[] | null) ?? [];
      }
    }
    // fetchById uses fromDbFormat (same as fetchAll), so result.data is already camelCase
    console.log('[ItemsStore] loadItemById', {
      id,
      workspace_id: (result.data as any)?.workspace_id,
      workspaceId: (result.data as any)?.workspaceId,
    });
    const migrated = attachFramesToItems([migrateItem(result.data)], frameRows)[0];
    set((state) => {
      const exists = state.items.some(c => c.id === id);
      const nextItems = exists
        ? state.items.map(c => (c.id === id ? migrated : c))
        : [...state.items, migrated];
      return { items: nextItems };
    });
  },
  
  // Getters
  getItemById: (id) => get().items.find(c => c.id === id),
  
  getUnreadyItems: () => get().items.filter(c => (c as any).exit_status === 'active' && !c.readyForRanking) as Item[],

  getItems: () => get().items.filter(c => c.lifecycle_state === 'item') as ShapedItem[],

  getPriorities: () => get().items.filter(c => (c as any).itemPoolId) as Priority[],

  getItemsByState: (state) => get().items.filter(c => c.lifecycle_state === state)
  })
);

// Initial fetch is driven by useSupabaseSync (App.tsx) to avoid duplicate requests and AbortErrors.